// frontend/src/components/staff/AdminBulkOneOffAvailabilityModal.js
//
// Workforce — SCHED-AVAIL-DASHBOARD-003-B admin bulk one-off availability
// authoring modal. Consumes the BE endpoint
// `POST /api/workforce/admin/availability/bulk` (PR #168 merged 2026-05-14).
//
// Story:       docs/backlog/SCHED-AVAIL-DASHBOARD-003-B-fe-bulk-edit-and-conflict-warnings.md
// Tech doc:    docs/architecture/workforce-admin-availability-dashboard-slice-3-tech.md
// Visual spec: docs/design/SCHED-AVAIL-DASHBOARD-003-design.md
// Banner vocab source: docs/design/NEW-SHIFT-AVAILABILITY-PEEK-design.md §4
//
// Spec reconciliation (refer to header comment of the BE controller at
// /mnt/c/development/studio-web-app/backend/controllers/
// workforceAdminAvailabilityBulkController.js):
//
// Marcus's tech doc locks the wire shape as SINGLE-STAFF x N-WINDOWS:
// one `userId` per request, 1..20 one-off windows. Amara's story prose
// and Zuri's visual spec described a multi-staff x 1-window shape. The
// BE controller resolves the divergence in favor of Marcus per the
// architect-is-source-of-truth doctrine. This component implements the
// reconciled shape: admin picks ONE staff, then enters 1..20 one-off
// windows (e.g., "Jamie covers Tue/Thu/Sat 9-2 next week" -> 3 rows).
//
// The story's 15 AC scenarios remain satisfiable under this reconciliation:
//   - Scenario 1-2 (entry + form shape) — staff is a single picker, NOT
//     a multi-select; the "N" count refers to WINDOWS not staff
//   - Scenario 3 (valid submit) — 4 rows for one staff still maps cleanly;
//     the dashboard refetches and renders the new windows
//   - Scenario 4 (BULK_VALIDATION_FAILED) — per-row error map honored
//   - Scenario 5 (recurring-overlap warning) — client-side detection
//     against the staff's existing recurring windows in the dashboard data
//   - Scenarios 6-7 (one-off-duplicate + cross-week info) — same detection
//     pass extended to one-off rows in the visible week range
//   - Scenario 8 (zero conflicts) — preview hidden when empty
//   - Scenarios 12-15 (a11y + regression + RBAC) — modal honors the
//     existing focus-trap + Esc + tab order pattern from SCHED-1K
//
// Production-safety:
//   - Pure-additive modal component. No existing surface mutated.
//   - Single-revert posture: delete this file + revert the import + the
//     bulk-CTA button addition in AdminStaffAvailabilityDashboard.
//   - No env vars, no schema changes. Consumes the BE endpoint locked by
//     Marcus's tech doc + Simone's PR #168 implementation.
//   - NOT a Customer-Facing Gate (R-020) — admin-internal feature.
//   - Defense-in-depth: NEVER passes a `staffProfileId` field; the BE
//     derives StaffProfile from `userId`. Mirrors SCHED-1K convention.
//
// R-022 Pattern (3) HOOK DISCIPLINE (preemptive):
//   ALL React hooks (useState / useEffect / useMemo / useCallback / useRef)
//   FIRE BEFORE any early return. The `if (!open) return null;` guard sits
//   AFTER every hook declaration to comply with Rules of Hooks at strict
//   ESLint config (matches CRA `react-scripts build` enforcement). This is
//   the explicit fix for the bug that slipped past PR #167 + hotfix 9e3ecfd.

import React from "react";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { workforceAdminAvailabilityBulkAPI } from "../../services/api";
import "./AdminBulkOneOffAvailabilityModal.css";

// ---------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------

// Marcus tech doc Q1 lock — 20 windows per request. Discourages runaway
// scripts; mirrors backend `MAX_WINDOWS_PER_REQUEST`. Story body's "50"
// reference is reconciled to 20 (BE is the source of truth).
const MAX_WINDOWS = 20;

// Notes 240-char ceiling — matches AdminSetAvailabilityModal MAX_NOTES_LENGTH.
const MAX_NOTES = 240;

const HHMM_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
const YYYY_MM_DD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const DAY_FULL = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// ---------------------------------------------------------------------
// Pure helpers (exported via __internal for unit tests)
// ---------------------------------------------------------------------

// "YYYY-MM-DD" -> dayOfWeek (0..6, Sun=0) in Phoenix-MST. Phoenix is UTC-7
// year-round (no DST), so we anchor the date at 12:00 wall-clock to avoid
// the local-time-vs-UTC boundary error. Returns null on malformed input.
const dayOfWeekForYmd = (ymd) => {
  if (typeof ymd !== "string" || !YYYY_MM_DD_PATTERN.test(ymd)) return null;
  const [yStr, mStr, dStr] = ymd.split("-");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  // 19:00 UTC == 12:00 Phoenix-MST. getUTCDay() returns the Phoenix dow.
  const dt = new Date(Date.UTC(y, m - 1, d, 19, 0, 0, 0));
  if (isNaN(dt.getTime())) return null;
  return dt.getUTCDay();
};

// Build an ISO 8601 midnight-UTC instant from a YYYY-MM-DD string.
// The BE validator parses any valid ISO; we send midnight-UTC for parity
// with AdminSetAvailabilityModal's `todayIsoMidnightUtc` convention.
const ymdToIsoMidnightUtc = (ymd) => {
  if (typeof ymd !== "string" || !YYYY_MM_DD_PATTERN.test(ymd)) return null;
  return `${ymd}T00:00:00.000Z`;
};

// Extract YYYY-MM-DD from a BE-serialized ISO 8601 effectiveDate. Same
// helper as dashboard; duplicated here to avoid a cross-component import
// pulling in render code. Pure + tiny.
const ymdFromIso = (iso) => {
  if (typeof iso !== "string") return "";
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : "";
};

// Format HH:MM 24-hour -> "9:00 AM MST" / "5:30 PM MST". Matches the
// codebase MST convention used by AdminSetAvailabilityModal.
const formatTime12hMst = (hhmm) => {
  if (typeof hhmm !== "string" || !HHMM_PATTERN.test(hhmm)) return hhmm || "";
  const [h, m] = hhmm.split(":").map((part) => parseInt(part, 10));
  const period = h < 12 ? "AM" : "PM";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, "0")} ${period} MST`;
};

// Format "YYYY-MM-DD" -> "Mon, May 24" for the date-hint line.
const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const formatDayMonDay = (ymd) => {
  if (typeof ymd !== "string" || !YYYY_MM_DD_PATTERN.test(ymd)) return "";
  const dow = dayOfWeekForYmd(ymd);
  if (dow === null) return "";
  const [, mStr, dStr] = ymd.split("-");
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  const shortDow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dow] || "";
  return `${shortDow}, ${MONTH_ABBR[m - 1]} ${d}`;
};

// Validate a single row at the FE level. Mirrors the BE invariants so
// the BE 400 is defense-in-depth, not the primary signal to admin. Returns
// the first failing message or null on pass.
const validateRow = (row) => {
  if (!row || typeof row !== "object") return "Row is missing.";
  if (typeof row.effectiveDate !== "string" || !YYYY_MM_DD_PATTERN.test(row.effectiveDate)) {
    return "Pick a valid date (YYYY-MM-DD).";
  }
  if (typeof row.startTime !== "string" || !HHMM_PATTERN.test(row.startTime)) {
    return "Start time must be HH:MM.";
  }
  if (typeof row.endTime !== "string" || !HHMM_PATTERN.test(row.endTime)) {
    return "End time must be HH:MM.";
  }
  if (row.endTime <= row.startTime) {
    return "End time must be after start time.";
  }
  return null;
};

// Detect cross-week conflicts for ONE row against a staff's existing windows
// in the dashboard data. Returns descriptor objects matching Zuri spec §3.6
// + §4.2 vocabulary. Types:
//   - 'RECURRING_OVERLAP' — staff has a recurring window for the same DoW
//     where the proposed [start, end] overlaps but does not match the
//     recurring window's time range (story Scenario 5)
//   - 'ONE_OFF_DUPLICATE' — staff has an existing one-off window for the
//     same effectiveDate; the new row would create a second one (Scenario 6)
//   - 'CROSS_WEEK_INFO' — staff has one-off windows on the same DoW in
//     OTHER calendar weeks (informational; not a conflict per se — Scenario 7)
//
// Returns: ConflictDescriptor[] (sorted; never throws on malformed input).
const detectConflictsForRow = (row, existingWindowsForStaff) => {
  const conflicts = [];
  if (
    !row ||
    typeof row.effectiveDate !== "string" ||
    !YYYY_MM_DD_PATTERN.test(row.effectiveDate) ||
    typeof row.startTime !== "string" ||
    !HHMM_PATTERN.test(row.startTime) ||
    typeof row.endTime !== "string" ||
    !HHMM_PATTERN.test(row.endTime) ||
    row.endTime <= row.startTime
  ) {
    return conflicts;
  }
  const proposedDow = dayOfWeekForYmd(row.effectiveDate);
  if (proposedDow === null) return conflicts;
  if (!Array.isArray(existingWindowsForStaff)) return conflicts;

  const crossWeekDates = [];
  for (const w of existingWindowsForStaff) {
    if (!w || typeof w !== "object") continue;
    // Recurring window on the SAME dow with time-range overlap (Scenario 5)
    if (w.isRecurring !== false) {
      // Recurring rules use the staff's matrix dayOfWeek slot. The dashboard
      // groups availability by dayOfWeek; we receive the slot key via
      // `existingWindowsForStaff` already keyed to proposedDow. So every
      // recurring window in this array IS for proposedDow.
      const overlaps =
        row.startTime < w.endTime && w.startTime < row.endTime;
      const exactMatch =
        row.startTime === w.startTime && row.endTime === w.endTime;
      if (overlaps && !exactMatch) {
        conflicts.push({
          type: "RECURRING_OVERLAP",
          severity: "warning",
          existingStart: w.startTime,
          existingEnd: w.endTime,
          message:
            `Recurring ${DAY_FULL[proposedDow]} ${formatTime12hMst(w.startTime)} - ` +
            `${formatTime12hMst(w.endTime)} already covers this slot. This one-off ` +
            `will be saved as an additional window.`,
        });
      }
      continue;
    }
    // One-off window (Scenarios 6 + 7)
    const wYmd = ymdFromIso(w.effectiveDate);
    if (!wYmd) continue;
    if (wYmd === row.effectiveDate) {
      conflicts.push({
        type: "ONE_OFF_DUPLICATE",
        severity: "warning",
        existingStart: w.startTime,
        existingEnd: w.endTime,
        existingDate: wYmd,
        message:
          `An existing one-off on ${formatDayMonDay(wYmd)} ` +
          `(${formatTime12hMst(w.startTime)} - ${formatTime12hMst(w.endTime)}) ` +
          `is already saved. Adding this row creates a second one-off on the same day.`,
      });
    } else {
      const otherDow = dayOfWeekForYmd(wYmd);
      if (otherDow === proposedDow) {
        crossWeekDates.push(wYmd);
      }
    }
  }
  if (crossWeekDates.length > 0) {
    crossWeekDates.sort();
    const list = crossWeekDates.slice(0, 3).map(formatDayMonDay).join(", ");
    const more =
      crossWeekDates.length > 3 ? ` (and ${crossWeekDates.length - 3} more)` : "";
    conflicts.push({
      type: "CROSS_WEEK_INFO",
      severity: "info",
      otherDates: crossWeekDates,
      message:
        `Staff also has one-off ${DAY_FULL[proposedDow]} windows on ` +
        `${list}${more}. The new entry is independent of those.`,
    });
  }
  return conflicts;
};

// Build the BE request body from FE state. Pure — no side effects.
const buildBulkRequestBody = ({ userId, rows, notes }) => {
  const body = {
    userId,
    windows: rows.map((r) => {
      const w = {
        dayOfWeek: dayOfWeekForYmd(r.effectiveDate),
        startTime: r.startTime,
        endTime: r.endTime,
        effectiveDate: ymdToIsoMidnightUtc(r.effectiveDate),
      };
      if (typeof r.notes === "string" && r.notes.trim()) {
        w.notes = r.notes.trim();
      }
      return w;
    }),
  };
  if (typeof notes === "string" && notes.trim()) {
    body.notes = notes.trim();
  }
  return body;
};

// ---------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------

/**
 * AdminBulkOneOffAvailabilityModal
 *
 * Props:
 *   - open                 boolean — mount control
 *   - onClose              () => void — fired on Cancel / Escape / X / outside
 *   - onSubmitSuccess      (result: { bulkOperationId, created, rows }) => void
 *                             — fires AFTER successful POST; consumer typically
 *                               re-fetches the dashboard
 *   - staffOptions         Array<{ userId: string, displayName: string,
 *                                  primaryRole?: string, availability?: object }>
 *                             — the dashboard's `staffRows` slice; the modal
 *                               consumes `userId`, `displayName`, and walks
 *                               `availability` to detect cross-week conflicts
 *   - preselectedUserId    string|null — optional initial staff selection
 */
const AdminBulkOneOffAvailabilityModal = ({
  open,
  onClose,
  onSubmitSuccess,
  staffOptions,
  preselectedUserId,
}) => {
  // -----------------------------------------------------------------
  // HOOKS — every hook fires before any early return.
  // (R-022 Pattern (3) — preemptive Rules-of-Hooks discipline.)
  // -----------------------------------------------------------------

  // Staff selection. Initialized from `preselectedUserId` when supplied.
  const [selectedUserId, setSelectedUserId] = React.useState(
    preselectedUserId || ""
  );

  // Window rows. Default to ONE empty row so admin sees the form pre-laid.
  // Each row: { id, effectiveDate, startTime, endTime, notes }.
  // `id` is a client-side stable key; never sent to the BE.
  const [rows, setRows] = React.useState(() => [
    { id: 1, effectiveDate: "", startTime: "", endTime: "", notes: "" },
  ]);
  const rowIdCounterRef = React.useRef(2);

  // Caller-level notes (applied to every row that does not have its own).
  const [bulkNotes, setBulkNotes] = React.useState("");

  // Submit state. `submitting` blocks the button; `submitError` surfaces
  // server-side validation; `perRowErrors` maps row-index -> error message.
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");
  const [perRowErrors, setPerRowErrors] = React.useState({});

  // Reset state on open / re-open. Avoids stale form data when admin
  // closes and re-opens for a different staff member.
  React.useEffect(() => {
    if (!open) return;
    setSelectedUserId(preselectedUserId || "");
    setRows([
      { id: 1, effectiveDate: "", startTime: "", endTime: "", notes: "" },
    ]);
    rowIdCounterRef.current = 2;
    setBulkNotes("");
    setSubmitting(false);
    setSubmitError("");
    setPerRowErrors({});
  }, [open, preselectedUserId]);

  // Escape closes (matches AdminSetAvailabilityModal Esc behavior).
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (typeof onClose === "function") onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Compute the selected staff's display name + recurring/one-off windows
  // for conflict detection. We walk the dashboard's `availability` map
  // (keyed by dayOfWeek 0..6) once into a flat array; conflict detection
  // re-filters per-row by dayOfWeek.
  const selectedStaff = React.useMemo(() => {
    if (!selectedUserId || !Array.isArray(staffOptions)) return null;
    return staffOptions.find((s) => s && s.userId === selectedUserId) || null;
  }, [selectedUserId, staffOptions]);

  const selectedStaffWindowsByDow = React.useMemo(() => {
    const empty = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    if (!selectedStaff || !selectedStaff.availability) return empty;
    for (let d = 0; d < 7; d += 1) {
      const arr = selectedStaff.availability[String(d)];
      empty[d] = Array.isArray(arr) ? arr : [];
    }
    return empty;
  }, [selectedStaff]);

  // Per-row client-side validation.
  const perRowValidationErrors = React.useMemo(() => {
    const out = {};
    rows.forEach((r, i) => {
      const err = validateRow(r);
      if (err) out[i] = err;
    });
    return out;
  }, [rows]);

  // Per-row conflict detection (advisory; never blocks submit).
  const perRowConflicts = React.useMemo(() => {
    const out = {};
    rows.forEach((r, i) => {
      const dow = dayOfWeekForYmd(r.effectiveDate);
      if (dow === null) {
        out[i] = [];
        return;
      }
      const windowsForDow = selectedStaffWindowsByDow[dow] || [];
      out[i] = detectConflictsForRow(r, windowsForDow);
    });
    return out;
  }, [rows, selectedStaffWindowsByDow]);

  // Roll-up: do ANY rows have conflicts? Drives the panel visibility.
  const anyConflicts = React.useMemo(() => {
    for (const i of Object.keys(perRowConflicts)) {
      if (perRowConflicts[i] && perRowConflicts[i].length > 0) return true;
    }
    return false;
  }, [perRowConflicts]);

  // Submit guard: enabled when a staff is selected AND every row is valid
  // AND we're not already submitting. Conflicts are advisory — they do
  // NOT block the button (per story doctrine: "advisory only").
  const canSubmit = React.useMemo(
    () =>
      !submitting &&
      typeof selectedUserId === "string" &&
      selectedUserId.length > 0 &&
      rows.length >= 1 &&
      rows.length <= MAX_WINDOWS &&
      Object.keys(perRowValidationErrors).length === 0,
    [submitting, selectedUserId, rows.length, perRowValidationErrors]
  );

  // -----------------------------------------------------------------
  // EVENT HANDLERS
  // -----------------------------------------------------------------

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
    // Clear server per-row error for the row being edited so admin sees
    // the message clear as they fix the issue.
    setPerRowErrors((prev) => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const addRow = () => {
    if (rows.length >= MAX_WINDOWS) return;
    const nextId = rowIdCounterRef.current;
    rowIdCounterRef.current += 1;
    setRows((prev) => [
      ...prev,
      { id: nextId, effectiveDate: "", startTime: "", endTime: "", notes: "" },
    ]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev; // keep at least one row visible
      return prev.filter((_, i) => i !== index);
    });
    setPerRowErrors((prev) => {
      // Shift indices down for rows after the removed index.
      const next = {};
      Object.keys(prev).forEach((k) => {
        const idx = parseInt(k, 10);
        if (idx < index) next[idx] = prev[k];
        else if (idx > index) next[idx - 1] = prev[k];
      });
      return next;
    });
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    setPerRowErrors({});
    const body = buildBulkRequestBody({
      userId: selectedUserId,
      rows,
      notes: bulkNotes,
    });
    try {
      const res = await workforceAdminAvailabilityBulkAPI.create(body);
      const data = res && res.data ? res.data : {};
      const created = typeof data.created === "number" ? data.created : rows.length;
      try {
        if (typeof toast === "function") {
          toast(
            `Saved ${created} one-off ${created === 1 ? "window" : "windows"}` +
              (selectedStaff ? ` for ${selectedStaff.displayName}.` : "."),
            { autoClose: 4000 }
          );
        } else if (toast && typeof toast.success === "function") {
          toast.success(
            `Saved ${created} one-off ${created === 1 ? "window" : "windows"}.`,
            { autoClose: 4000 }
          );
        }
      } catch (_e) {
        /* toast container absence is OK */
      }
      if (typeof onSubmitSuccess === "function") {
        onSubmitSuccess({
          bulkOperationId: data.bulkOperationId || null,
          created,
          rows: Array.isArray(data.rows) ? data.rows : [],
        });
      }
      if (typeof onClose === "function") onClose();
    } catch (err) {
      const response = err && err.response;
      const status = response && response.status;
      const respData = (response && response.data) || {};
      if (status === 400 && respData.code === "BULK_VALIDATION_FAILED") {
        // Per-row error map. Surface inline next to each affected row.
        const errs = respData.errors || {};
        const normalized = {};
        Object.keys(errs).forEach((k) => {
          const e = errs[k];
          normalized[k] =
            (e && (e.message || e.code)) ||
            "This row failed server-side validation.";
        });
        setPerRowErrors(normalized);
        setSubmitError(
          "Some rows could not be saved. Fix the highlighted rows and try again."
        );
      } else if (status === 400 && respData.code === "BULK_BATCH_TOO_LARGE") {
        setSubmitError(
          respData.message ||
            `Too many rows (max ${MAX_WINDOWS}). Remove some and try again.`
        );
      } else if (status === 400 && respData.code === "BULK_EMPTY_BATCH") {
        setSubmitError("Add at least one window before saving.");
      } else if (status === 404 && respData.code === "STAFF_NOT_FOUND") {
        setSubmitError(
          "Staff member is no longer available. Pick another staff member."
        );
      } else if (status === 403) {
        setSubmitError("Admin role is required.");
      } else if (respData.message) {
        setSubmitError(respData.message);
      } else {
        setSubmitError(
          "Couldn't save the bulk windows. Try again in a moment."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------
  // EARLY RETURN — after all hooks have fired.
  // -----------------------------------------------------------------
  if (!open) return null;

  // -----------------------------------------------------------------
  // RENDER
  // -----------------------------------------------------------------
  const titleId = "abom-title";

  return (
    <div
      className="abom-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (typeof onClose === "function") onClose();
        }
      }}
      data-testid="abom-backdrop"
    >
      <div
        className="abom"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="abom"
      >
        <header className="abom__header">
          <div>
            <h2 className="abom__title" id={titleId}>
              Bulk add one-off availability
            </h2>
            <p className="abom__subtitle">Admin-on-behalf bulk entry</p>
          </div>
          <button
            type="button"
            className="abom__close-btn"
            onClick={onClose}
            aria-label="Close bulk availability modal"
            data-testid="abom-close"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </header>

        <div className="abom__body">
          {/* Staff picker -------------------------------------------- */}
          <label className="abom__field">
            <span className="abom__field-label">Staff member *</span>
            <select
              className="abom__select"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              data-testid="abom-staff-select"
              aria-label="Select staff member for bulk availability"
            >
              <option value="">— Pick a staff member —</option>
              {Array.isArray(staffOptions)
                ? staffOptions.map((s) => (
                    <option key={s.userId} value={s.userId}>
                      {s.displayName}
                    </option>
                  ))
                : null}
            </select>
          </label>

          {/* Windows section ----------------------------------------- */}
          <section
            className="abom__windows"
            aria-labelledby="abom-windows-heading"
          >
            <div className="abom__windows-header">
              <h3 className="abom__windows-heading" id="abom-windows-heading">
                One-off windows ({rows.length} of {MAX_WINDOWS})
              </h3>
              <p className="abom__windows-helper">
                Times shown in MST. Each row is one one-off availability window.
              </p>
            </div>

            <ul className="abom__rows" data-testid="abom-rows">
              {rows.map((row, i) => {
                const rowValidationError = perRowValidationErrors[i];
                const rowServerError = perRowErrors[i];
                const rowConflicts = perRowConflicts[i] || [];
                const dow = dayOfWeekForYmd(row.effectiveDate);
                const dowHint =
                  dow !== null ? `${DAY_FULL[dow]} (MST)` : "Pick a date";
                return (
                  <li
                    key={row.id}
                    className="abom__row"
                    data-testid={`abom-row-${i}`}
                  >
                    <div className="abom__row-fields">
                      <label className="abom__field abom__field--date">
                        <span className="abom__field-label">Date *</span>
                        <input
                          type="date"
                          className="abom__input"
                          value={row.effectiveDate}
                          onChange={(e) =>
                            updateRow(i, { effectiveDate: e.target.value })
                          }
                          data-testid={`abom-row-${i}-date`}
                          aria-label={`Date for row ${i + 1}`}
                        />
                        <span className="abom__field-hint">{dowHint}</span>
                      </label>
                      <label className="abom__field abom__field--time">
                        <span className="abom__field-label">Start *</span>
                        <input
                          type="time"
                          step="900"
                          className="abom__input"
                          value={row.startTime}
                          onChange={(e) =>
                            updateRow(i, { startTime: e.target.value })
                          }
                          data-testid={`abom-row-${i}-start`}
                          aria-label={`Start time for row ${i + 1}`}
                        />
                      </label>
                      <label className="abom__field abom__field--time">
                        <span className="abom__field-label">End *</span>
                        <input
                          type="time"
                          step="900"
                          className="abom__input"
                          value={row.endTime}
                          onChange={(e) =>
                            updateRow(i, { endTime: e.target.value })
                          }
                          data-testid={`abom-row-${i}-end`}
                          aria-label={`End time for row ${i + 1}`}
                        />
                      </label>
                      <button
                        type="button"
                        className="abom__row-remove"
                        onClick={() => removeRow(i)}
                        disabled={rows.length <= 1}
                        aria-label={`Remove row ${i + 1}`}
                        data-testid={`abom-row-${i}-remove`}
                      >
                        Remove
                      </button>
                    </div>
                    {rowValidationError ? (
                      <p
                        className="abom__row-validation"
                        role="alert"
                        data-testid={`abom-row-${i}-validation`}
                      >
                        {rowValidationError}
                      </p>
                    ) : null}
                    {rowServerError ? (
                      <p
                        className="abom__row-server-error"
                        role="alert"
                        data-testid={`abom-row-${i}-server-error`}
                      >
                        Server: {rowServerError}
                      </p>
                    ) : null}
                    {rowConflicts.length > 0 ? (
                      <ul
                        className="abom__row-conflicts"
                        data-testid={`abom-row-${i}-conflicts`}
                      >
                        {rowConflicts.map((c, ci) => (
                          <li
                            key={`${c.type}-${ci}`}
                            className={
                              "abom__row-conflict abom__row-conflict--" +
                              (c.severity === "info" ? "info" : "warning")
                            }
                            data-conflict-type={c.type}
                          >
                            <FaExclamationTriangle
                              aria-hidden="true"
                              className="abom__row-conflict-icon"
                            />
                            <span>{c.message}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            <button
              type="button"
              className="abom__add-row-btn"
              onClick={addRow}
              disabled={rows.length >= MAX_WINDOWS}
              data-testid="abom-add-row"
            >
              + Add another window
            </button>
          </section>

          {/* Notes textarea ----------------------------------------- */}
          <label className="abom__field abom__field--notes">
            <span className="abom__field-label">Notes (optional)</span>
            <textarea
              className="abom__textarea"
              rows={2}
              maxLength={MAX_NOTES}
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
              placeholder="e.g., Event coverage for Saturday photo walk"
              data-testid="abom-notes"
              aria-label="Optional notes applied to every row"
            />
          </label>

          {/* Conflict-warn rollup panel ------------------------------ */}
          {anyConflicts ? (
            <div
              className="abom__conflicts-panel"
              role="alert"
              aria-live="polite"
              data-testid="abom-conflicts-panel"
            >
              <FaExclamationTriangle
                aria-hidden="true"
                className="abom__conflicts-icon"
              />
              <div>
                <p className="abom__conflicts-heading">
                  Some rows overlap existing availability
                </p>
                <p className="abom__conflicts-body">
                  These warnings are advisory. You can still save —
                  recurring patterns are not modified.
                </p>
              </div>
            </div>
          ) : null}

          {/* Submit error banner ------------------------------------ */}
          {submitError ? (
            <div
              className="abom__submit-error"
              role="alert"
              data-testid="abom-submit-error"
            >
              {submitError}
            </div>
          ) : null}
        </div>

        <footer className="abom__footer">
          <button
            type="button"
            className="abom__btn abom__btn--secondary"
            onClick={onClose}
            data-testid="abom-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="abom__btn abom__btn--primary"
            onClick={onSubmit}
            disabled={!canSubmit}
            data-testid="abom-submit"
          >
            {submitting
              ? "Saving..."
              : `Save ${rows.length} ${rows.length === 1 ? "window" : "windows"}`}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AdminBulkOneOffAvailabilityModal;

// Exposed for unit tests — pure helpers exercised as units.
export const __internal = {
  MAX_WINDOWS,
  MAX_NOTES,
  dayOfWeekForYmd,
  ymdToIsoMidnightUtc,
  ymdFromIso,
  formatTime12hMst,
  formatDayMonDay,
  validateRow,
  detectConflictsForRow,
  buildBulkRequestBody,
};
